import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { useAdminDirectory } from '@/hooks/useAdmins';
import { useAuth } from '@/hooks/useAuth';
import { useChatEndpoints } from '@/hooks/useChatEndpoints';
import { useForms } from '@/hooks/useForms';
import { useMailAccounts } from '@/hooks/useMailAccounts';
import {
  useNotificationsSettings,
  useSesIdentityStatus,
  useUpdateNotificationsSettings,
} from '@/hooks/useSettings';

/** SES VerificationStatus → バッジ表示のマッピング。 */
function sesStatusBadge(status: string): {
  variant: 'approved' | 'draft' | 'danger' | 'default';
  label: string;
} {
  switch (status) {
    case 'SUCCESS':
      return { variant: 'approved', label: '検証済み' };
    case 'PENDING':
      return { variant: 'draft', label: '検証保留中' };
    case 'FAILED':
    case 'TEMPORARY_FAILURE':
      return { variant: 'danger', label: '検証失敗' };
    default:
      return { variant: 'default', label: status || '未開始' };
  }
}

/** 停滞商談しきい値の既定 / 有効範囲。functions/src/deals/scheduled.ts と対で維持する。 */
const STALE_DEAL_DAYS_DEFAULT = 7;
const STALE_DEAL_DAYS_MIN = 1;
const STALE_DEAL_DAYS_MAX = 365;

const INHERIT_VALUE = '__inherit__';
const UNSET_ENDPOINT = '__unset__';

/** 設定カード。フラットな羅列だと境界が読めないため 1 セクション = 1 カードにする。 */
const SECTION_CLASS =
  'rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg-surface)] p-5';
const SECTION_TITLE_CLASS =
  'text-[15px] font-bold text-[var(--text-primary)] mb-4 pb-3 border-b border-[var(--border)]';

/**
 * ラベル + 現在値バッジ。
 * 選択系の設定は「そもそも設定済みなのか」がドロップダウンを開かないと分からず
 * 未保存と誤認しやすいため、設定済み / 未設定を常時表示する。
 */
function FieldHeader({
  label,
  isSet,
  setLabel = '設定中',
  unsetLabel = '未設定',
}: {
  label: string;
  isSet: boolean;
  setLabel?: string;
  unsetLabel?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 mb-1.5">
      <Label className="mb-0">{label}</Label>
      <Badge variant={isSet ? 'approved' : 'default'}>{isSet ? setLabel : unsetLabel}</Badge>
    </div>
  );
}

interface FormValues {
  gmailFromAddress: string;
  gmailDailySummaryEnabled: boolean;
  gmailDailySummaryTime: string;
  googleChatEnabled: boolean;
  notifyOnNewUser: boolean;
  notifyOnLowRating: boolean;
  notifyOnError: boolean;
  notifyOnDeliveryCodeRename: boolean;
  notifyOnMailSendFailure: boolean;
  /** 保留トレイ (商談マージ候補) に新規候補が入ったとき */
  notifyOnMergeCandidate: boolean;
  /** 停滞商談の通知 */
  notifyOnStaleDeal: boolean;
  /** 停滞とみなす日数 (notifyStaleDeals のしきい値。1〜365) */
  staleDealThresholdDays: number;
  /** 保留トレイの新規候補を「個人宛」に通知する設定 (2026-08-18) */
  mergeCandidateNotify: {
    email: boolean;
    personalChat: boolean;
    recipientUids: string[];
  };
  /** 招待 / システム通知メールの送信元 mail_account ID（null = 既定にフォールバック）。 */
  invitationFromAccountId: string | null;
  /** Google Chat 通知の送信先 chat_endpoints ドキュメント ID (null = 未設定 / 通知スキップ) */
  googleChatNotifyEndpointId: string | null;
  /** 一括配信: 日跨ぎ分割を有効化 */
  mailBulkCrossDaySplitEnabled: boolean;
  /** 一括配信: SMTP リレー方式の既定送信元アカウント ID (null = 未設定) */
  mailBulkRelayAccountId: string | null;
  /** 配信停止フォーム（フォーム管理>マーケティング）の forms ドキュメント ID (null = 機能オフ) */
  unsubscribeFormId: string | null;
}

const defaults: FormValues = {
  gmailFromAddress: '',
  gmailDailySummaryEnabled: false,
  gmailDailySummaryTime: '09:00',
  googleChatEnabled: false,
  notifyOnNewUser: true,
  notifyOnLowRating: true,
  notifyOnError: true,
  notifyOnDeliveryCodeRename: true,
  notifyOnMailSendFailure: true,
  notifyOnMergeCandidate: true,
  notifyOnStaleDeal: true,
  staleDealThresholdDays: STALE_DEAL_DAYS_DEFAULT,
  mergeCandidateNotify: { email: true, personalChat: true, recipientUids: [] },
  invitationFromAccountId: null,
  googleChatNotifyEndpointId: null,
  mailBulkCrossDaySplitEnabled: false,
  mailBulkRelayAccountId: null,
  unsubscribeFormId: null,
};

export function NotificationsSettingsTab() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const { data, isLoading } = useNotificationsSettings();
  const { data: mailAccounts } = useMailAccounts();
  const { data: chatEndpoints } = useChatEndpoints('google_chat');
  const { data: forms } = useForms();
  // 保留トレイ通知の宛先候補。listMemberDirectory は外部SA を返さないので内部メンバーのみ。
  const { data: memberDirectory } = useAdminDirectory({ enabled: isOwner });
  const updateMutation = useUpdateNotificationsSettings();

  const form = useForm<FormValues>({ defaultValues: defaults });
  const invitationAccountValue = form.watch('invitationFromAccountId');
  const staleDealEnabled = form.watch('notifyOnStaleDeal');
  // 保存バーの状態表示用。formState はプロキシなので参照して初めて購読される。
  const { isDirty } = form.formState;
  const chatEndpointValue = form.watch('googleChatNotifyEndpointId');
  const googleChatEnabled = form.watch('googleChatEnabled');
  const bulkRelayAccountValue = form.watch('mailBulkRelayAccountId');
  const unsubscribeFormValue = form.watch('unsubscribeFormId');
  const mergeNotifyRecipients = form.watch('mergeCandidateNotify.recipientUids');

  const [setupOpen, setSetupOpen] = useState(false);
  const sesStatus = useSesIdentityStatus(isOwner);

  useEffect(() => {
    if (!data) return;
    // ネストした mergeCandidateNotify はスプレッドだと丸ごと置換されて
    // 未保存キー (email / personalChat) が undefined になるため個別にマージする。
    form.reset({
      ...defaults,
      ...data,
      mergeCandidateNotify: {
        ...defaults.mergeCandidateNotify,
        ...(data.mergeCandidateNotify ?? {}),
        recipientUids: data.mergeCandidateNotify?.recipientUids ?? [],
      },
    });
  }, [data, form]);

  const onSubmit = async (values: FormValues) => {
    try {
      // number 入力は空欄だと NaN になり Firestore が受け付けないため、
      // 保存前に既定値へフォールバック + 有効範囲へクランプする。
      const rawDays = Number(values.staleDealThresholdDays);
      const staleDealThresholdDays = Number.isFinite(rawDays)
        ? Math.min(STALE_DEAL_DAYS_MAX, Math.max(STALE_DEAL_DAYS_MIN, Math.round(rawDays)))
        : STALE_DEAL_DAYS_DEFAULT;
      await updateMutation.mutateAsync({ ...values, staleDealThresholdDays });
      toast.success('通知設定を保存しました');
    } catch (err) {
      toast.error(`保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-3xl space-y-4 pb-2">
      {!isOwner && (
        <div className="rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--bg-elevated)] px-4 py-3 text-[13px] text-[var(--text-secondary)]">
          閲覧のみのモードです。通知設定の変更には owner 権限が必要です。
        </div>
      )}

      <section className={SECTION_CLASS}>
        <h3 className={SECTION_TITLE_CLASS}>Gmail 通知</h3>
        <div className="space-y-3">
          <div>
            <Label htmlFor="gmailFromAddress">送信元アドレス</Label>
            <Input
              id="gmailFromAddress"
              type="email"
              placeholder="noreply@caudesign.jp"
              {...form.register('gmailFromAddress')}
              disabled={!isOwner}
            />
          </div>
          <Switch
            label="日次サマリを送信"
            description="LINE Bot の前日 24 時間のメトリクスを owner 全員に送信"
            {...form.register('gmailDailySummaryEnabled')}
            disabled={!isOwner}
          />
          <div>
            <Label htmlFor="gmailDailySummaryTime">送信時刻 (JST)</Label>
            <Input
              id="gmailDailySummaryTime"
              type="time"
              {...form.register('gmailDailySummaryTime')}
              disabled={!isOwner}
            />
          </div>
        </div>
      </section>

      <section className={SECTION_CLASS}>
        <h3 className={SECTION_TITLE_CLASS}>管理者通知メール（招待 / システム通知）</h3>
        <div>
          <FieldHeader
            label="送信元メールアカウント"
            isSet={!!invitationAccountValue}
            unsetLabel="既定にフォールバック"
          />
          <Select
            value={invitationAccountValue ?? INHERIT_VALUE}
            onValueChange={(v) =>
              form.setValue('invitationFromAccountId', v === INHERIT_VALUE ? null : v, {
                shouldDirty: true,
              })
            }
            disabled={!isOwner}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={INHERIT_VALUE}>
                既定（isDefault=true のアカウント）にフォールバック
              </SelectItem>
              {(mailAccounts ?? [])
                .filter((a) => a.status === 'active')
                .map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.displayName} &lt;{a.email}&gt;{a.isDefault ? '（既定）' : ''}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed mt-2">
            管理者招待や将来のシステム通知メールに使う送信元です。営業オペの自動メール（見積/請求/発注）とは別系統です。
          </p>
        </div>
      </section>

      <section className={SECTION_CLASS}>
        <h3 className={SECTION_TITLE_CLASS}>一括配信（大量送信）</h3>
        <div className="space-y-3">
          <Switch
            label="日跨ぎ分割を有効化"
            description="送信元アカウントの 1 予約あたり上限（Gmail 約 2000 / SMTP リレー既定 10000）を超える宛先でも予約を拒否せず、上限を 1 日分として超過分を翌日以降へ自動繰り越して送信します。2 日目以降は初回と同じ時刻に送信されます。"
            {...form.register('mailBulkCrossDaySplitEnabled')}
            disabled={!isOwner}
          />
          <div>
            <FieldHeader label="SMTP リレー方式の既定送信元" isSet={!!bulkRelayAccountValue} />
            <Select
              value={bulkRelayAccountValue ?? UNSET_ENDPOINT}
              onValueChange={(v) =>
                form.setValue('mailBulkRelayAccountId', v === UNSET_ENDPOINT ? null : v, {
                  shouldDirty: true,
                })
              }
              disabled={!isOwner}
            >
              <SelectTrigger>
                <SelectValue placeholder="-- 未設定 --" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNSET_ENDPOINT}>
                  未設定（isDefault のアカウントを既定選択）
                </SelectItem>
                {(mailAccounts ?? [])
                  .filter((a) => a.status === 'active')
                  .map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.displayName} &lt;{a.email}&gt;
                      {a.provider === 'smtp' ? '（SMTP）' : '（Gmail）'}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed mt-2">
              大量送信に使う SMTP リレー（Amazon SES
              等）のアカウントを指定すると、配信予約フォームの送信元が初期状態でこのアカウントに選択されます。
            </p>
          </div>

          <div className="pt-3 border-t border-[var(--border)]">
            <FieldHeader label="配信停止フォーム" isSet={!!unsubscribeFormValue} />
            <Select
              value={unsubscribeFormValue ?? UNSET_ENDPOINT}
              onValueChange={(v) =>
                form.setValue('unsubscribeFormId', v === UNSET_ENDPOINT ? null : v, {
                  shouldDirty: true,
                })
              }
              disabled={!isOwner}
            >
              <SelectTrigger>
                <SelectValue placeholder="-- 未設定（配信停止機能オフ）--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNSET_ENDPOINT}>
                  未設定（配信停止フッターを挿入しない）
                </SelectItem>
                {(forms ?? [])
                  .filter((f) => (f.tag ?? 'marketing') === 'marketing')
                  .map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.title || f.name || '(無題)'}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed mt-2">
              マーケティングフォームの中から「配信停止用フォーム」を 1
              つ指定します。指定すると、次の 3 つが自動で動きます。
            </p>
            <ol className="mt-2 space-y-1.5 text-[13px] text-[var(--text-secondary)] leading-relaxed">
              <li className="flex gap-2">
                <span className="text-[var(--text-muted)] select-none">1.</span>
                <span>
                  一括 /
                  予約配信メールの末尾に、配信停止リンクを小さな文字で自動挿入します（業務メールには入りません）。
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-[var(--text-muted)] select-none">2.</span>
                <span>
                  そのフォームから送信されたメールアドレスと一致するオーディエンスに
                  <strong className="text-[var(--text-primary)]">「配信停止」</strong>
                  タグを付与し、以降の一括配信から自動除外します。
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-[var(--text-muted)] select-none">3.</span>
                <span>
                  別のマーケティングフォームに再度回答すると、タグを外して配信先として自動復活します。
                </span>
              </li>
            </ol>
          </div>

          <div className="pt-3 border-t border-[var(--border)]">
            <div className="flex items-center justify-between mb-2">
              <div>
                <Label className="mb-0">Amazon SES セットアップ</Label>
                <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed mt-1">
                  SMTP リレー（SES）の開通手順と、送信ドメインの検証ステータスを確認できます。
                </p>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setSetupOpen(true)}
              >
                セットアップ手順
              </Button>
            </div>

            <div className="rounded-[var(--radius-sm)] bg-[var(--bg-elevated)] border border-[var(--border)] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] font-semibold">
                  SES 検証ステータス
                  {sesStatus.data?.region && (
                    <span className="text-xs text-[var(--text-muted)] font-normal ml-2">
                      ({sesStatus.data.region})
                    </span>
                  )}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => sesStatus.refetch()}
                  disabled={sesStatus.isFetching || !isOwner}
                >
                  <RefreshCw className={sesStatus.isFetching ? 'animate-spin' : ''} />
                  更新
                </Button>
              </div>

              {sesStatus.isLoading ? (
                <Spinner />
              ) : sesStatus.isError ? (
                <p className="text-xs text-[var(--accent-red)]">
                  取得に失敗しました:{' '}
                  {sesStatus.error instanceof Error
                    ? sesStatus.error.message
                    : String(sesStatus.error)}
                </p>
              ) : sesStatus.data && !sesStatus.data.configured ? (
                <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed">
                  AWS 認証情報が未登録です。
                  <strong>設定 &gt; ④シークレット管理</strong> で「AWS SES アクセスキー ID /
                  シークレットアクセスキー」を登録し、
                  <code className="bg-[var(--bg-hover)] px-1 rounded">
                    getSesIdentityStatus
                  </code>{' '}
                  関数を再デプロイすると、ここに検証状態が表示されます。
                </p>
              ) : sesStatus.data && sesStatus.data.identities.length === 0 ? (
                <p className="text-[13px] text-[var(--text-secondary)]">
                  送信ドメイン ID がまだ登録されていません。
                </p>
              ) : (
                <div className="space-y-1.5">
                  {sesStatus.data?.identities.map((id) => {
                    const badge = sesStatusBadge(id.verificationStatus);
                    return (
                      <div
                        key={id.name}
                        className="flex items-center justify-between gap-3 text-[13px]"
                      >
                        <span className="font-mono truncate">{id.name}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {id.sendingEnabled ? (
                            <span className="text-[11px] font-medium text-[var(--accent-green)]">
                              送信可
                            </span>
                          ) : (
                            <span className="text-[11px] text-[var(--text-secondary)]">
                              送信不可
                            </span>
                          )}
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Amazon SES SMTP リレー セットアップ手順</DialogTitle>
            <DialogDescription>
              1 アカウントから一括 3000 件超を送るための SMTP リレー（SES）開通手順です。
            </DialogDescription>
          </DialogHeader>
          <ol className="list-decimal list-inside space-y-2 text-[13px] text-[var(--text-secondary)] leading-relaxed">
            <li>
              <strong className="text-[var(--text-primary)]">送信ドメインの DKIM 認証</strong>： SES
              に送信ドメイン ID を作成し（Easy DKIM / RSA_2048）、発行された 3 本の CNAME
              を各ドメインの DNS に追加。 さくらは値を末尾ドット付きの絶対 FQDN で入力。
            </li>
            <li>
              <strong className="text-[var(--text-primary)]">検証完了の確認</strong>： 上の「SES
              検証ステータス」が <Badge variant="approved">検証済み</Badge>{' '}
              になるまで待つ（通常数分〜数十分、最大 72h）。
            </li>
            <li>
              <strong className="text-[var(--text-primary)]">本番アクセス申請</strong>： SES
              サンドボックス解除を申請（月間想定送信数・用途・バウンス/苦情の扱いを記入、承認
              ≈24h）。
            </li>
            <li>
              <strong className="text-[var(--text-primary)]">SMTP 認証情報の発行</strong>： SES →
              SMTP 設定で SMTP ユーザーを作成。 発行されたパスワードは{' '}
              <strong>設定 &gt; ②通知 / メールアカウント</strong> の SMTP
              アカウントとして直接登録（チャットやメモに残さない）。
            </li>
            <li>
              <strong className="text-[var(--text-primary)]">送信枠の調整</strong>： 必要なら当該
              SMTP アカウントの{' '}
              <code className="bg-[var(--bg-hover)] px-1 rounded">dailySendLimit</code> を SES
              実枠に合わせて設定。
            </li>
          </ol>
          <div className="rounded-[var(--radius-sm)] bg-[var(--bg-elevated)] border border-[var(--border)] p-3 text-[13px] text-[var(--text-secondary)] leading-relaxed">
            ※ ステータス照会用の <strong>AWS IAM アクセスキー</strong>（手順 4 の SMTP
            認証情報とは別物）は、
            <strong>設定 &gt; ④シークレット管理</strong>の「AWS SES アクセスキー ID /
            シークレットアクセスキー」に登録します。 最小権限は{' '}
            <code className="bg-[var(--bg-hover)] px-1 rounded">ses:ListEmailIdentities</code>{' '}
            のみ。
          </div>
        </DialogContent>
      </Dialog>

      <section className={SECTION_CLASS}>
        <h3 className={SECTION_TITLE_CLASS}>Google Chat 通知</h3>
        <div className="space-y-3">
          <Switch
            label="Google Chat を有効化"
            description="管理者通知を Google Chat スペースに送信"
            {...form.register('googleChatEnabled')}
            disabled={!isOwner}
          />
          <div>
            <FieldHeader label="送信先スペース" isSet={!!chatEndpointValue} />
            <Select
              value={chatEndpointValue ?? UNSET_ENDPOINT}
              onValueChange={(v) =>
                form.setValue('googleChatNotifyEndpointId', v === UNSET_ENDPOINT ? null : v, {
                  shouldDirty: true,
                })
              }
              disabled={!isOwner || !googleChatEnabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="-- 選択 --" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNSET_ENDPOINT}>未設定（通知スキップ）</SelectItem>
                {(chatEndpoints ?? [])
                  .filter((ep) => ep.isActive)
                  .map((ep) => (
                    <SelectItem key={ep.id} value={ep.id}>
                      {ep.name}
                      {ep.description ? ` — ${ep.description}` : ''}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed mt-2">
              チャット管理タブで登録した webhook から 1
              件を選択。複数管理する場合は用途別に切り替えてください。
            </p>
          </div>
          <div className="pt-2 border-t border-[var(--border)] space-y-2">
            <Switch
              label="新規 LINE ユーザー登録時に通知"
              {...form.register('notifyOnNewUser')}
              disabled={!isOwner}
            />
            <Switch
              label="LINE 低評価（rating ≤ 2）を受信したとき"
              {...form.register('notifyOnLowRating')}
              disabled={!isOwner}
            />
            <Switch
              label="重大エラー発生時"
              {...form.register('notifyOnError')}
              disabled={!isOwner}
            />
            <Switch
              label="配信コード自動改名時"
              {...form.register('notifyOnDeliveryCodeRename')}
              disabled={!isOwner}
            />
            <Switch
              label="Mail 送信失敗時"
              {...form.register('notifyOnMailSendFailure')}
              disabled={!isOwner}
            />
            <Switch
              label="保留トレイに新規申込が入ったとき"
              description="商談マージ候補 (確信度 40% 以上)。確定するまで商談ボードにも SA ポータルにも出ないため、滞留に気付くための通知"
              {...form.register('notifyOnMergeCandidate')}
              disabled={!isOwner}
            />
            <Switch
              label="商談が一定期間停滞したとき"
              description="lastTouchedAt が下記日数を超えた open 商談を毎朝 9:00 に通知。停滞しても失注へは自動遷移しない（失注は手動確定のみ）ため、放置商談に気付くための通知"
              {...form.register('notifyOnStaleDeal')}
              disabled={!isOwner}
            />
            <div className="pl-1">
              <Label htmlFor="staleDealThresholdDays">停滞とみなす日数</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  id="staleDealThresholdDays"
                  type="number"
                  min={STALE_DEAL_DAYS_MIN}
                  max={STALE_DEAL_DAYS_MAX}
                  step={1}
                  className="w-24"
                  {...form.register('staleDealThresholdDays', { valueAsNumber: true })}
                  disabled={!isOwner || !staleDealEnabled}
                />
                <span className="text-sm text-[var(--text-secondary)]">日</span>
              </div>
              <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed mt-2">
                既定 {STALE_DEAL_DAYS_DEFAULT} 日（{STALE_DEAL_DAYS_MIN}〜{STALE_DEAL_DAYS_MAX}{' '}
                日）。 商材のリードタイムが長い場合は伸ばしてください。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className={SECTION_CLASS}>
        <h3 className={SECTION_TITLE_CLASS}>保留トレイ — 担当者への個別通知</h3>
        <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed mb-4">
          上の Google Chat スペース宛通知とは別に、指定したメンバーへ個別に届けます。 個人の Google
          Chat webhook / Chatwork ルーム ID は、各メンバーの
          <strong>ユーザー詳細 &gt; 通知設定</strong>（掲示板通知と同じ設定）を使います。 Chatwork
          はシークレット管理に{' '}
          <code className="bg-[var(--bg-hover)] px-1 rounded">CHATWORK_API_TOKEN</code>
          が登録されている場合のみ送信されます。
        </p>
        <div className="space-y-3">
          <Switch
            label="メールで通知"
            {...form.register('mergeCandidateNotify.email')}
            disabled={!isOwner}
          />
          <Switch
            label="個人チャット (Google Chat / Chatwork) で通知"
            {...form.register('mergeCandidateNotify.personalChat')}
            disabled={!isOwner}
          />
          <div className="pt-2 border-t border-[var(--border)]">
            <Label>通知するメンバー</Label>
            <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed mt-2 mb-2.5">
              未選択の場合、個別通知は送信されません（Google Chat スペース宛の通知のみ）。
            </p>
            <div className="space-y-1.5 max-h-64 overflow-y-auto rounded-sm border border-[var(--border)] p-2">
              {(memberDirectory ?? []).filter((m) => m.level !== 'external').length === 0 ? (
                <p className="text-xs text-[var(--text-muted)] px-1 py-2">
                  対象メンバーがいません。
                </p>
              ) : (
                (memberDirectory ?? [])
                  .filter((m) => m.level !== 'external')
                  .map((m) => {
                    const checked = (mergeNotifyRecipients ?? []).includes(m.uid);
                    return (
                      <label
                        key={m.uid}
                        className="flex items-center gap-2 text-xs cursor-pointer px-1 py-1 rounded-sm hover:bg-[var(--bg-hover)]"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!isOwner}
                          onChange={(e) => {
                            const cur = mergeNotifyRecipients ?? [];
                            form.setValue(
                              'mergeCandidateNotify.recipientUids',
                              e.target.checked ? [...cur, m.uid] : cur.filter((u) => u !== m.uid),
                              { shouldDirty: true },
                            );
                          }}
                        />
                        <span className="font-medium">
                          {m.fullName || m.displayName || m.email}
                        </span>
                        <span className="text-[var(--text-muted)]">{m.email}</span>
                        {m.resolvedStatus !== 'active' && (
                          <Badge variant="draft" className="text-[10px]">
                            {m.resolvedStatus}
                          </Badge>
                        )}
                      </label>
                    );
                  })
              )}
            </div>
          </div>
        </div>
      </section>

      {isOwner && (
        <div className="sticky bottom-0 z-10 flex items-center gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg-surface)] px-4 py-3 shadow-[var(--shadow)]">
          <span
            className={
              isDirty
                ? 'text-[13px] font-medium text-[var(--accent-amber)]'
                : 'text-[13px] text-[var(--text-secondary)]'
            }
          >
            {isDirty ? '未保存の変更があります' : 'すべて保存済みです'}
          </span>
          <div className="ml-auto flex gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => data && form.reset()}
              disabled={updateMutation.isPending || !isDirty}
            >
              取り消し
            </Button>
            <Button type="submit" variant="primary" disabled={updateMutation.isPending || !isDirty}>
              {updateMutation.isPending ? '保存中...' : '保存'}
            </Button>
          </div>
        </div>
      )}
    </form>
  );
}
